package mapsearch

import apiclient.geoobjects.GeoObjectDetails
import apiclient.geoobjects.ObjectTags
import apiclient.geoobjects.ObjectType
import apiclient.tags.getUniqueTag
import dev.fritz2.core.RootStore
import dev.fritz2.core.storeOf
import koin.withKoin
import kotlinx.coroutines.Job
import mapsearch.buildings.BuildingSearchSpecStore
import mapsearch.generalMarkers.GeneralMarkersSearchSpecStore
import mapsearch.globalsearch.toMapMarker
import mapsearch.historyPathTool.DisplayedPathObjectResultsStore
import mapsearch.zones.ZoneSearchSpecStore

class MapRenderOverrides {

    /**
     * HiddenMarkers
     */

    val hiddenMarkers: MutableSet<String> = mutableSetOf()

    fun addHiddenOverrides(ids: Set<String>) {
        withKoin {
            val clusterService: ClusterService = get()
            val generalMarkersSearchSpecStore: GeneralMarkersSearchSpecStore = get()
            val zoneSearchSpecStore: ZoneSearchSpecStore = get()

            // add to hidden markers lists
            hiddenMarkers.addAll(ids)

            clusterService.removeFeaturesFromMap(ids.toList())
            generalMarkersSearchSpecStore.removeGeneralMarkersFromMap(ids.toList())
            zoneSearchSpecStore.removeZonesFromMap(ids.toList())
        }
    }

    fun addHiddenOverride(id: String) {
        addHiddenOverrides(setOf(id))
    }

    fun removeHiddenOverrides(ids: Set<String>) {
        hiddenMarkers.removeAll(ids)
    }

    fun removeHiddenOverride(id: String) {
        removeHiddenOverrides(setOf(id))
    }

    /**
     * PathToolHighlightedMarkers (If not empty, all others get desaturated)
     */

    val pathToolActive = storeOf(false, Job())

    val pathToolHighlightedObjectIds = storeOf(setOf<String>(), Job())

    fun setPathToolHighlightOverrides(ids: Set<String>) {
        pathToolHighlightedObjectIds.update(ids)
    }

    fun removePathToolHighlightOverrides(ids: Set<String>) {
        pathToolHighlightedObjectIds.update(pathToolHighlightedObjectIds.current - ids)
    }

    fun removePathToolHighlightOverride(id: String) {
        removePathToolHighlightOverrides(setOf(id))
    }

    /**
     * Central handling of active object markers
     * Directly apply active marker state
     * - re-render the newly active marker
     * - re-render the last active marker
     */

    var activeObject: GeoObjectDetails? = null
    var activeZoneObject: GeoObjectDetails? = null
    var activeGeneralMarkerObject: GeoObjectDetails? = null
    var activeBuildingObject: GeoObjectDetails? = null

    private fun setActiveObject(newActiveObj: GeoObjectDetails?, isNew: Boolean = false) {
        if (newActiveObj?.id != activeObject?.id) {
            withKoin {
                val clusterService: ClusterService = get()
                if (isNew) {
                    clusterService.updateFeatures(
                        updatedMarkers = listOfNotNull(
                            activeObject?.toMapMarker(),
                        ),
                        newMarkers = listOfNotNull(newActiveObj?.toMapMarker()),
                    )
                } else {
                    clusterService.updateFeatures(
                        listOfNotNull(
                            activeObject?.toMapMarker(),
                            newActiveObj?.toMapMarker(),
                        ),
                    )
                }

                activeObject = newActiveObj
            }
        }
    }

    private fun setActiveZone(newActiveObj: GeoObjectDetails?) {
//            console.log("ActiveZoneMarker - last: ${current?.title} -> new: ${newActiveObj?.title}")
        if (newActiveObj?.id != activeZoneObject?.id) {
            withKoin {
                val zoneSearchSpecStore: ZoneSearchSpecStore = get()
                activeZoneObject?.let { last ->
                    zoneSearchSpecStore.reRenderSingleZone(zone = last)
                }
                newActiveObj?.let { new ->
                    zoneSearchSpecStore.reRenderSingleZone(zone = new)
                }

                activeZoneObject = newActiveObj
            }
        }
    }

    private fun setActiveGeneralMarker(newActiveObj: GeoObjectDetails?) {
        if (newActiveObj?.id != activeGeneralMarkerObject?.id) {
            withKoin {
                val generalMarkerSearchSpecStore: GeneralMarkersSearchSpecStore = get()
                activeGeneralMarkerObject?.let { last ->
                    generalMarkerSearchSpecStore.reRenderSingleGeneralMarkerAndShapes(generalMarker = last)
                }
                newActiveObj?.let { new ->
                    generalMarkerSearchSpecStore.reRenderSingleGeneralMarkerAndShapes(generalMarker = new)
                }
                activeGeneralMarkerObject = newActiveObj
            }
        }
    }

    private fun setActiveBuilding(newActiveObj: GeoObjectDetails?) {
//            console.log("ActiveBuildingMarker - last: ${current?.title} -> new: ${newActiveObj?.title}")
        if (newActiveObj?.id != activeBuildingObject?.id) {
            withKoin {
                val buildingSearchSpecStore: BuildingSearchSpecStore = get()
                activeBuildingObject?.let { last ->
                    buildingSearchSpecStore.reRenderSingleBuildingMarker(building = last)
                }
                newActiveObj?.let { new ->
                    buildingSearchSpecStore.reRenderSingleBuildingMarker(building = new)
                }
                activeBuildingObject = newActiveObj
            }
        }
    }

    fun removeLastObjects() {
        activeObject = null
        activeZoneObject = null
        activeGeneralMarkerObject = null
        activeBuildingObject = null
    }

    fun pushActiveObject(obj: GeoObjectDetails?, isNew: Boolean = false) {
//        console.log("^ PUSH ACTIVE ${obj?.objectType?.name}:", obj?.title)
        when (obj?.objectType) {
            ObjectType.POI, ObjectType.Task, ObjectType.Event,
            ObjectType.ObjectMarker -> {
                setActiveObject(obj, isNew)
                setActiveGeneralMarker(null)
                setActiveZone(null)
                setActiveBuilding(null)
            }

            ObjectType.GeneralMarker -> {
                setActiveObject(null)
                setActiveGeneralMarker(obj)
                setActiveZone(null)
                setActiveBuilding(null)
            }

            ObjectType.Zone -> {
                setActiveObject(null)
                setActiveGeneralMarker(null)
                setActiveZone(obj)
                setActiveBuilding(null)
            }

            ObjectType.Building -> {
                setActiveObject(null)
                setActiveGeneralMarker(null)
                setActiveZone(null)
                setActiveBuilding(obj)
            }

            else -> {
                setActiveObject(null)
                setActiveGeneralMarker(null)
                setActiveZone(null)
                setActiveBuilding(null)
            }
        }
    }

    /**
     * Handle ActiveHistoryEntryMarker separately
     */

    class ActiveHistoryEntryMarker : ActiveMarkerHandler() {
        override fun set(newActiveObj: GeoObjectDetails?) {
//            console.log("ActiveHistoryEntryMarker - last: ${current?.title} -> new: ${newActiveObj?.title}")
            if (newActiveObj?.id != current?.id) {
                withKoin {
                    val displayedPathObjectResultsStore: DisplayedPathObjectResultsStore = get()
                    current?.let { last ->
                        last.tags.getUniqueTag(ObjectTags.ConnectedTo)
                            ?.let { displayedPathObjectResultsStore.reRenderSingleHistoryEventMarker(objectId = it, last.id) }
                    }
                    newActiveObj?.let { new ->
                        new.tags.getUniqueTag(ObjectTags.ConnectedTo)?.let {
                            displayedPathObjectResultsStore.reRenderSingleHistoryEventMarker(objectId = it, new.id)
                        }
                    }
                    update(newActiveObj)
                }
            }
        }
    }

    private val activeHistoryEntryMarker = ActiveHistoryEntryMarker()

    fun pushActiveHistoryEntry(obj: GeoObjectDetails?) {
        if (obj?.objectType == ObjectType.HistoryEntry) {
            activeHistoryEntryMarker.set(obj)
        } else activeHistoryEntryMarker.set(null)
    }
}

abstract class ActiveMarkerHandler : RootStore<GeoObjectDetails?>(null, Job()) {

    abstract fun set(newActiveObj: GeoObjectDetails?)

    fun getActiveObj(): GeoObjectDetails? {
        return current
    }

}
